import * as React from 'react';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, {
  toggleButtonGroupClasses,
} from '@mui/material/ToggleButtonGroup';
import { INSIGHTS_ANALYSIS_TAB_ITEMS } from '../types';
import { useTranslation } from 'react-i18next';

type Tab = {
  main?: boolean;
  label: string;
  value: string;
  icon?: React.ReactNode;
  children?: Tab[];
};

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  border: 'none',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  fontWeight: 400,
  boxShadow: 'none',

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },

  '&.Mui-selected': {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',

    '&:hover': {
      backgroundColor: theme.palette.grey[300],
    },
  },

  '&:focus': {
    outline: 'none',
  },
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.25),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]:
    {
      marginLeft: theme.spacing(1),
      borderLeft: 'none',
    },
}));

const SubTabs = ({
  value,
  handleChange,
  subtabs,
}: {
  value: string;
  handleChange: (value: INSIGHTS_ANALYSIS_TAB_ITEMS) => void;
  subtabs?: Tab[];
}) => {
  const { t } = useTranslation();
  if (!subtabs || subtabs.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        display: 'grid',
        border: 'none',
        margin: '0 20px',
        backgroundColor: theme.palette.grey[100],
        borderRadius: '8px',
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        width: 'fit-content',
      })}
    >
      <StyledToggleButtonGroup
        size="small"
        value={value}
        exclusive
        onChange={(_, newValue) => {
          handleChange(newValue);
        }}
        aria-label="subtab selection"
      >
        {subtabs.map((subtab) => (
          <StyledToggleButton
            key={subtab.value}
            value={subtab.value}
            aria-label={subtab.label}
            sx={{
              minWidth: '80px',
            }}
          >
            {t(subtab.label)}
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>
    </Paper>
  );
};

export default SubTabs;
